import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { LiaCloudUploadAltSolid } from 'react-icons/lia';

interface FileUploadDropzoneProps {
  title?: string;
  onFileSelect?: (file: File) => void;
  accept?: string;
  className?: string;
  /** 未指定の場合は .pdf, .doc, .docx のみ許可。プレビュー用に ()=>true で全ファイル許可も可 */
  validateFileType?: (file: File) => boolean;
}

const defaultValidateFileType = (file: File): boolean => {
  const allowedExtensions = ['.pdf', '.doc', '.docx'];
  const fileName = file.name.toLowerCase();
  return allowedExtensions.some((ext) => fileName.endsWith(ext));
};

export function FileUploadDropzone({
  title,
  onFileSelect,
  accept = '*/*',
  className,
  validateFileType,
}: FileUploadDropzoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const isValidFileType = useCallback(
    (file: File): boolean =>
      validateFileType ? validateFileType(file) : defaultValidateFileType(file),
    [validateFileType]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        const file = files[0];
        if (!isValidFileType(file)) {
          alert('PDF、Wordファイル（.pdf, .doc, .docx）のみアップロード可能です。');
          return;
        }
        setSelectedFile(file);
        onFileSelect?.(file);
      }
    },
    [onFileSelect, isValidFileType]
  );

  const _handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (!isValidFileType(file)) {
          alert('PDF、Wordファイル（.pdf, .doc, .docx）のみアップロード可能です。');
          return;
        }
        setSelectedFile(file);
        onFileSelect?.(file);
      }
    },
    [onFileSelect, isValidFileType]
  );

  const handleButtonClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        if (!isValidFileType(file)) {
          alert('PDF、Wordファイル（.pdf, .doc, .docx）のみアップロード可能です。');
          return;
        }
        setSelectedFile(file);
        onFileSelect?.(file);
      }
    };
    input.click();
  }, [accept, onFileSelect, isValidFileType]);

  return (
    <div className={clsx('flex flex-col gap-3', className)}>
      {title && <h3 className="text-xl font-bold text-[#101010]">{title}</h3>}

      <div className="border border-solid border-[#101010] bg-white p-4 sm:p-6">
        <div
          className={clsx(
            'flex flex-col items-center justify-center gap-4 p-4 sm:flex-row sm:p-6',
            'rounded border-2 border-dashed bg-[#F9F9F9]',
            'transition-colors duration-200',
            isDragOver ? 'border-[#106EE0] bg-[rgba(16,110,224,0.2)]' : 'border-[#CDCDCD]'
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* アップロードアイコン */}
          <div className="flex size-10 items-center justify-center">
            <LiaCloudUploadAltSolid
              size={40}
              className={clsx(
                'transition-colors duration-200',
                isDragOver ? 'text-[#106EE0]' : 'text-[#767676]'
              )}
            />
          </div>

          {/* テキスト - スマホでは非表示 */}
          <div className="hidden text-center sm:block">
            <p className="text-lg font-bold text-[#262626]">
              {selectedFile ? selectedFile.name : 'ここにドラッグアンドドロップ'}
            </p>
          </div>

          {/* ファイル選択ボタン */}
          <button
            onClick={handleButtonClick}
            className={clsx(
              'w-full max-w-[174px] border-[1.5px] border-solid border-[#101010] bg-white px-4 py-2',
              'text-base font-bold text-[#101010]',
              'transition-colors duration-200 hover:bg-gray-50'
            )}
          >
            ファイルを選択
          </button>

          {/* 選択されたファイル名をスマホで表示 */}
          {selectedFile && (
            <div className="block text-center sm:hidden">
              <p className="text-sm font-bold break-all text-[#262626]">{selectedFile.name}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
